import { useState } from "react";
import * as yup from "yup";
import { addDays, differenceInDays } from "date-fns";
import {
    createDateFromDDMMYYYY,
    formatDateYYYYMMDD,
    isBusinessDay,
    nextBusinessDay,
    businessAdd,
} from "@icarius-utils/date";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import { useDispatch } from "react-redux";
import { getLocalizedString } from "@icarius-localization/strings";
import useIsSocietyCountry from "@icarius-app/components/wrapper/hooks/useIsSocietyCountry";

const useHandleForm = (
    data,
    createCallback,
    modifyCallback,
    invalidDataCallback,
    selects,
    presetEmployee,
    startHolidaysOnNonWorkingDay,
    allowHalfDayHolidays
) => {
    const dispatch = useDispatch();

    const isCreate = !Boolean(data);
    const isPeru = useIsSocietyCountry("PE");

    const createInitialData = () => {
        const getInitialFieldData = (fieldName) => {
            if (fieldName.includes("Fecha")) {
                if (data?.[fieldName]) return createDateFromDDMMYYYY(data[fieldName]);
                if (["Fecha desde", "Fecha desde de aplicación"].includes(fieldName)) return new Date();
                return null;
            }

            if (fieldName === "Código de empleado") {
                if (presetEmployee) return selects.employees.find((item) => item.key === presetEmployee);
                return isCreate ? null : selects.employees.find((item) => item.key === data[fieldName]);
            }

            if (fieldName === "Tiempo por día" && !allowHalfDayHolidays && isCreate) {
                return 1;
            }

            if (isPeru && isCreate && fieldName === "Tipo de vacaciones") {
                return "UV";
            }

            return isCreate ? "" : data[fieldName];
        };

        const fieldNames = [
            "Código de empleado",
            "Tipo de vacaciones",
            "Fecha desde",
            "Cantidad de días",
            "Fecha hasta",
            "Fecha desde de aplicación",
            "Fecha hasta de aplicación",
            "Tiempo por día",
            "Fecha de retorno",
            "Comentarios",
        ];

        return fieldNames.reduce((initialData, fieldName) => {
            return {
                ...initialData,
                [fieldName]: getInitialFieldData(fieldName),
            };
        }, {});
    };

    const [formData, setFormData] = useState(createInitialData());

    const dataIsValid = async () => {
        const schema = yup.object().shape({
            "Código de empleado": yup.object().required(),
            "Tipo de vacaciones": yup.string().required(),
            "Fecha desde": yup.date().required(),
            "Cantidad de días": yup.string().required(),
            "Fecha hasta": yup.date(),
            "Fecha desde de aplicación": yup.date().required(),
            "Fecha hasta de aplicación": yup.date(),
            "Tiempo por día": yup.string().required(),
            "Fecha de retorno": yup.date().nullable(true),
        });

        return await schema.isValid(formData).then((valid) => valid);
    };

    const submit = async () => {
        if (await dataIsValid()) {
            let initialDay = createDateFromDDMMYYYY(formData["Fecha desde"]);
            // Si tengo habilitado lo de parametros generales, no puedo arrancar las vacaciones en dia no habil
            if (
                !startHolidaysOnNonWorkingDay &&
                !isBusinessDay(initialDay, [1, 2, 3, 4, 5], formData["Código de empleado"]?.holidays || [])
            ) {
                dispatch(
                    openSnackbarAction({
                        duration: null,
                        msg: getLocalizedString("startDateCannotBeWeekendOrHoliday"),
                        severity: "error",
                    })
                );
                return;
            }

            if (isCreate) {
                const dataToSend = {
                    ...formData,
                    "Código de empleado": formData["Código de empleado"].key,
                    "Fecha desde": formatDateYYYYMMDD(formData["Fecha desde"]),
                    "Fecha hasta": formatDateYYYYMMDD(formData["Fecha hasta"]),
                    "Fecha desde de aplicación": formatDateYYYYMMDD(formData["Fecha desde de aplicación"]),
                    "Fecha hasta de aplicación": formatDateYYYYMMDD(formData["Fecha hasta de aplicación"]),
                    "Fecha de retorno": formatDateYYYYMMDD(formData["Fecha de retorno"]),
                };

                createCallback(dataToSend);
                return true;
            }

            modifyCallback({ Comentarios: formData["Comentarios"], internalCode: data.internalCode });
            return true;
        } else {
            invalidDataCallback();
            return false;
        }
    };

    const calculateEndDate = (initialDate, employee, vacationTypeCode, amountOfDays) => {
        if (initialDate && employee && vacationTypeCode && amountOfDays) {
            const vacationType = selects.vacationTypes.find((item) => item.key === vacationTypeCode);

            const workingWeekdays = [1, 2, 3, 4, 5];
            const baseDate = initialDate instanceof Date ? initialDate : createDateFromDDMMYYYY(initialDate);

            if (!vacationType.includeHolidaysAndWeekends) {
                let initialDayBD = baseDate;
                // Tengo que avanzar hasta el proximo dia habil, en caso de que este no lo sea
                if (!isBusinessDay(initialDayBD, workingWeekdays, employee.holidays)) {
                    initialDayBD = nextBusinessDay(initialDayBD, workingWeekdays, employee.holidays);
                }

                return businessAdd(initialDayBD, amountOfDays - 1, workingWeekdays, employee.holidays);
            } else {
                return addDays(baseDate, amountOfDays - 1);
            }
        }

        return null;
    };

    const calculateEndApplicationDate = (initialDate, startDate, endDate) => {
        if (initialDate && startDate && endDate) {
            const daysToAdd2 = differenceInDays(new Date(endDate), new Date(startDate));
            const baseDate = initialDate instanceof Date ? initialDate : createDateFromDDMMYYYY(initialDate);
            return addDays(baseDate, daysToAdd2);
        }

        return null;
    };

    const calculateReturnDate = (initialDate, employee) => {
        if (initialDate && employee) {
            let initialDay2 = initialDate instanceof Date ? initialDate : createDateFromDDMMYYYY(initialDate);

            // Tengo que avanzar hasta el proximo dia habil
            initialDay2 = nextBusinessDay(initialDay2, [1, 2, 3, 4, 5], employee.holidays);

            return initialDay2;
        }

        return null;
    };

    const setFormValue = (value, fieldName) => {
        // si cambia alguno de los que afecta al calculo de fecha final, recalcular la fecha hasta y fecha hasta de aplicación
        const altersEndDate = [
            "Tipo de vacaciones",
            "Código de empleado",
            "Cantidad de días",
            "Fecha desde",
            "Fecha desde de aplicación",
        ];
        if (altersEndDate.includes(fieldName)) {
            let employee = formData["Código de empleado"];
            let typeCode = formData["Tipo de vacaciones"];
            let amountOfDays = formData["Cantidad de días"];
            let dateFrom = formData["Fecha desde"];
            let appDateFrom = formData["Fecha desde de aplicación"];
            let appDateTo;

            if (fieldName === "Tipo de vacaciones") {
                typeCode = value;
                amountOfDays = ""; //limpiar cantidad de días
            }

            if (fieldName === "Código de empleado") {
                employee = value;
                amountOfDays = ""; //limpiar cantidad de días
            }

            if (fieldName === "Cantidad de días") {
                amountOfDays = value;
            }

            if (fieldName === "Fecha desde") {
                dateFrom = value;
            }

            if (fieldName === "Fecha desde de aplicación") {
                appDateFrom = value;
            }

            const dateTo = calculateEndDate(dateFrom, employee, typeCode, amountOfDays);

            if (fieldName === "Fecha desde") {
                appDateFrom = dateFrom; // si cambia fecha desde, fecha desde de aplicacion va a tener el mismo valor
                appDateTo = dateTo; // si fecha desde y fecha desde de aplicacion son iguales, la fecha hasta tambien
            } else {
                appDateTo = calculateEndApplicationDate(appDateFrom, dateFrom, dateTo);
            }

            setFormData({
                ...formData,
                [fieldName]: value,
                "Fecha hasta": dateTo,
                "Fecha desde de aplicación": appDateFrom,
                "Fecha hasta de aplicación": appDateTo,
                "Fecha de retorno": calculateReturnDate(dateTo, employee),
            });
            return;
        }

        setFormData({
            ...formData,
            [fieldName]: value,
        });
    };

    return { isCreate, formData, setFormValue, submit };
};

export default useHandleForm;
